/* istanbul ignore file */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fromJS } from 'immutable';
import styles from '../styles/HotelSlider.scss';
import { HotelCard } from './HotelCard';
import { setSelectedHotels } from '../ducks';
import { MODULE_NAME } from '../utils/constants';

export const HotelSlider = ({ hotels = [] }) => {
  const [index, setIndex] = useState(0);
  const containerRef = useRef(null);
  const slideRefs = useRef([]);
  const selectedHotels = useSelector((state) => state.getIn(['modules', MODULE_NAME, 'selectedHotels'])).toJS();
  const dispatch = useDispatch();

  const SLIDE_FRACTION = 0.85;

  slideRefs.current = [];

  const setSlideRef = (el, i) => {
    slideRefs.current[i] = el;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateLayout = () => {
      const containerW = container.clientWidth;
      const slideW = containerW * SLIDE_FRACTION;
      const pad = (containerW - slideW) / 2;

      container.style.paddingRight = `${pad}px`;

      slideRefs.current.forEach((el) => {
        // eslint-disable-next-line no-param-reassign -- required reassign
        if (el) el.style.width = `${slideW}px`;
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(updateLayout);
    });

    const ro = new ResizeObserver(updateLayout);
    ro.observe(container);

    // eslint-disable-next-line consistent-return -- need to disconnect
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const i = Number(entry.target.dataset.index);

          // Only switch when slide is clearly dominant
          if (entry.intersectionRatio > 0.6) {
            setIndex(i);
          }
        });
      },
      {
        root: container,
        threshold: [0.6], // only fire when 60% visible
      }
    );

    slideRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    // eslint-disable-next-line consistent-return -- needed for counter
    return () => observer.disconnect();
  }, [hotels.length]);

  const selectHotel = (hotel) => {
    const exists = selectedHotels?.some(
      (h) => h.hotelId === hotel.hotelId
    );

    let updated;

    if (exists) {
      updated = selectedHotels.filter(
        (h) => h.hotelId !== hotel.hotelId
      );
    } else {
      updated = [...selectedHotels, hotel];
    }

    dispatch(setSelectedHotels(fromJS(updated)));
  };

  return (
    <div className="position-relative">
      <div
        ref={containerRef}
        className={`${styles.hotelSlider} ${styles.reverseMargin}`}
        role="region"
        aria-roledescription="carousel"
        aria-label="Recommendations carousel"
      >
        {hotels.map((hotel, i) => {
          const isSelected = selectedHotels?.some(
            (h) => h.hotelId === hotel.hotelId
          );

          return (
            <div
              key={hotel.hotelId ?? i}
              ref={(el) => setSlideRef(el, i)}
              data-index={i}
              className={`${styles.hotelSlide} ${
                i === index ? styles.isActive : ''
              }`}
              aria-roledescription="slide"
              aria-label={`${hotel.name} (${i + 1} of ${hotels.length})`}
            >
              <HotelCard
                hotel={hotel}
                isSelected={isSelected}
                selectHotel={selectHotel}
              />
            </div>
          );
        })}
      </div>

      <div className={styles.sliderCounter} aria-live="polite">
        {index + 1} of {hotels.length}
      </div>
    </div>
  );
};
HotelSlider.propTypes = {
  hotels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    src: PropTypes.string,
    promotion: PropTypes.string,
  })).isRequired,
};
